import { useEffect, useState, useRef } from "react";
import { usePlayerStats } from "./usePlayerStats";
import { useBowlerStats } from "./useBowlerStats";

export default function useInningsEngine({
  battingTeam,
  bowlingTeam,
  oversLimit = 20,
  target = null,
  enabled = true,
  resetKey,
}) {
  /* ---------------- CORE STATE ---------------- */
  const [score, setScore] = useState(0);
  const [wickets, setWickets] = useState(0);
  const [overs, setOvers] = useState(0);
  const [balls, setBalls] = useState(0);

  const [striker, setStriker] = useState(null);
  const [nonStriker, setNonStriker] = useState(null);
  const [bowler, setBowler] = useState(null);

  const [isInningsOver, setIsInningsOver] = useState(false);
  const [lastBall, setLastBall] = useState(null);

  const [pendingWicket, setPendingWicket] = useState(null);
  const [isOverComplete, setIsOverComplete] = useState(false);


  /* ---------------- REFS ---------------- */
  const processingRef = useRef(false);
  const nonStrikerRef = useRef(null);
  const nextBatsmanIndexRef = useRef(2); // striker + nonStriker already used

  /* ---------------- PLAYER / BOWLER STATS ---------------- */
  const {
    players,
    initPlayer,
    addBattingEvent,
    setOut,
    resetPlayers,
  } = usePlayerStats();

  const {
    bowlers,
    initBowler,
    addBowlingEvent,
    resetBowlers,
  } = useBowlerStats();

  /* ---------------- KEEP REF SYNC ---------------- */
  useEffect(() => {
    nonStrikerRef.current = nonStriker;
  }, [nonStriker]);

  /* ---------------- RESET BATTING ORDER ---------------- */
  useEffect(() => {
    nextBatsmanIndexRef.current = 2;
  }, [resetKey]);

  /* ---------------- HARD RESET (NEW INNINGS) ---------------- */
  useEffect(() => {
    setScore(0);
    setWickets(0);
    setOvers(0);
    setBalls(0);

    setStriker(null);
    setNonStriker(null);
    setBowler(null);

    setIsInningsOver(false);
    setLastBall(null);

    processingRef.current = false;
    nonStrikerRef.current = null;

    resetPlayers();
    resetBowlers();

    setPendingWicket(null);
    setIsOverComplete(false);
  }, [resetKey]);

  /* ---------------- INIT PLAYERS ---------------- */
  useEffect(() => {
    if (!enabled) return;
    battingTeam?.players?.forEach(initPlayer);
    bowlingTeam?.players?.forEach(initBowler);
  }, [enabled, battingTeam, bowlingTeam]);

  /* ---------------- STRIKE SWAP ---------------- */
  const swapStrike = () => {
    setStriker((current) => {
      setNonStriker(current);
      return nonStrikerRef.current;
    });
  };

  /* ---------------- BOWL BALL ---------------- */
  const bowlBall = ({
    runs = 0,
    isWide = false,
    isNoBall = false,
    isWicket = false,
    wicketType = null,
    fielderId = null,
  }) => {
    if (
      !enabled ||
      isInningsOver ||
      !striker ||
      !nonStriker ||
      !bowler ||
      processingRef.current
    ) {
      return;
    }

    processingRef.current = true;

    const isLegal = !isWide && !isNoBall;
    const extraRuns = isWide || isNoBall ? 1 : 0;
    const totalRuns = runs + extraRuns;

    /* ---------------- UPDATE STATS ---------------- */
    addBattingEvent(striker.player_id, runs, isLegal);
    addBowlingEvent({
      bowlerId: bowler.player_id,
      runs: totalRuns,
      isLegal,
      isWicket,
    });

    setScore((s) => s + totalRuns);

    let nextOver = overs;
    let nextBall = balls;

    /* ---------------- LEGAL BALL FIRST ---------------- */
    if (isLegal) {
      nextBall += 1;

      if (nextBall === 6) {
        nextBall = 0;
        nextOver += 1;
        setOvers(nextOver);
        setBowler(null);
        setIsOverComplete(true); // 🔥 FORCE BOWLER SELECTION
      }


      setBalls(nextBall);
    }

    /* ---------------- SAVE LAST BALL ---------------- */
    setLastBall({
      over: nextOver,
      ball: nextBall,
      runs,
      extras: extraRuns,
      extraType: isWide ? "wide" : isNoBall ? "no-ball" : null,
      isWicket,
      wicketType,
      fielderId,
      striker,
      nonStriker,
      bowler,
      timestamp: Date.now(),
    });

    /* ---------------- WICKET ---------------- */
    if (isWicket) {
      setWickets((w) => w + 1);

      // ⛔ DO NOT mark out yet
      setPendingWicket({
        wicketType,
        fielderId,
      });

      processingRef.current = false;
      return;
    }



    /* ---------------- STRIKE ROTATION ---------------- */
    if (isLegal) {
      // Odd runs → swap strike (if over not ended)
      if (runs % 2 === 1 && nextBall !== 0) {
        swapStrike();
      }

      // End of over → swap strike
      if (nextBall === 0) {
        swapStrike();
      }
    }

    /* ---------------- END CONDITIONS ---------------- */
   const nextWickets = isWicket ? wickets + 1 : wickets;

    if (
      nextWickets >= battingTeam.players.length - 1 ||
      nextOver >= oversLimit ||
      (target !== null && score + totalRuns >= target)
    ) {
      setIsInningsOver(true);
    }

    processingRef.current = false;
  };

  /* ---------------- SELECT NEXT BATTER ---------------- */
    const selectNextBatter = (player, outBatsmanId) => {
      if (!pendingWicket) return;

      // 🔥 Mark correct batsman out
      setOut(outBatsmanId, pendingWicket.wicketType);

      // 🔁 Replace the out batsman
      if (striker?.player_id === outBatsmanId) {
        setStriker(player);
      } else {
        setNonStriker(player);
      }

      setPendingWicket(null);
    };


    /* ---------------- SELECT NEXT BOWLER ---------------- */
    const selectNextBowler = (player) => {
      setBowler(player);
      setIsOverComplete(false);
    };

  /* ---------------- SAFE DISABLED RETURN ---------------- */
  if (!enabled) {
    return {
      score: 0,
      wickets: 0,
      overs: 0,
      balls: 0,
      isInningsOver: false,
      striker: null,
      nonStriker: null,
      bowler: null,
      setStriker: () => {},
      setNonStriker: () => {},
      setBowler: () => {},
      players: {},
      bowlers: {},
      lastBall: null,
      bowlBall: () => {},
      selectNextBatter: () => {},
      selectNextBowler: () => {},
      pendingWicket: null,
      isOverComplete: false,
    };
  }

  /* ---------------- PUBLIC API ---------------- */
  return {
    score,
    wickets,
    overs,
    balls,
    isInningsOver,
    striker,
    nonStriker,
    bowler,
    setStriker,
    setNonStriker,
    setBowler,
    players,
    bowlers,
    lastBall,
    bowlBall,
    pendingWicket,
    isOverComplete,
    selectNextBatter,
    selectNextBowler,

  };
}
